import React, { useState, useEffect } from "react";
import { X, Mail, Lock, User as UserIcon, Loader2, Crown } from "lucide-react";
import { useApp } from "../context/AppContext";

export default function AccountModal() {
  const { accountOpen, setAccountOpen, user, updateProfile } = useApp();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (accountOpen && user) {
      setName(user.name || "");
      setEmail(user.email || "");
      setCurrentPassword("");
      setNewPassword("");
      setError("");
    }
  }, [accountOpen, user]);

  if (!accountOpen || !user) return null;

  const emailChanged = email.trim().toLowerCase() !== (user.email || "");
  const wantsPassword = newPassword.length > 0;
  const needsCurrent = emailChanged || wantsPassword;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email.trim()) {
      setError("Email cannot be empty.");
      return;
    }
    if (wantsPassword && newPassword.length < 6) {
      setError("New password must be at least 6 characters.");
      return;
    }
    if (needsCurrent && !currentPassword) {
      setError("Enter your current password to confirm changes.");
      return;
    }
    const payload = { name: name.trim(), email: email.trim() };
    if (needsCurrent) payload.current_password = currentPassword;
    if (wantsPassword) payload.new_password = newPassword;

    setBusy(true);
    const res = await updateProfile(payload);
    setBusy(false);
    if (res.ok) {
      setAccountOpen(false);
    } else {
      setError(res.message);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setAccountOpen(false)} />
      <div className="relative z-10 w-full max-w-[420px] rounded-3xl bg-[#101012] border border-white/10 p-6">
        <button onClick={() => setAccountOpen(false)} className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/5 flex items-center justify-center hover:bg-white/10">
          <X size={16} />
        </button>

        <h3 className="text-[13px] font-semibold tracking-[0.16em] uppercase">Account Settings</h3>
        {user.is_free_access && (
          <span className="inline-flex items-center gap-1 mt-2 text-[10px] text-amber-300 bg-amber-400/10 px-2 py-0.5 rounded-full">
            <Crown size={10} /> Full Access — kept after changing your details
          </span>
        )}

        <form onSubmit={submit} className="mt-5 space-y-3">
          <div>
            <span className="text-[11px] uppercase tracking-wide text-white/40">Display name</span>
            <div className="mt-1 flex items-center gap-2 px-3 h-11 rounded-xl bg-white/[0.05] border border-white/10">
              <UserIcon size={16} className="text-white/40" />
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" className="flex-1 bg-transparent outline-none text-[14px] text-white placeholder:text-white/35" />
            </div>
          </div>

          <div>
            <span className="text-[11px] uppercase tracking-wide text-white/40">Email</span>
            <div className="mt-1 flex items-center gap-2 px-3 h-11 rounded-xl bg-white/[0.05] border border-white/10">
              <Mail size={16} className="text-white/40" />
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className="flex-1 bg-transparent outline-none text-[14px] text-white placeholder:text-white/35" />
            </div>
          </div>

          <div className="pt-1 border-t border-white/5">
            <span className="text-[11px] uppercase tracking-wide text-white/40">New password (optional)</span>
            <div className="mt-1 flex items-center gap-2 px-3 h-11 rounded-xl bg-white/[0.05] border border-white/10">
              <Lock size={16} className="text-white/40" />
              <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Leave blank to keep current" className="flex-1 bg-transparent outline-none text-[14px] text-white placeholder:text-white/35" />
            </div>
          </div>

          {needsCurrent && (
            <div>
              <span className="text-[11px] uppercase tracking-wide text-amber-300/70">Current password (required to confirm)</span>
              <div className="mt-1 flex items-center gap-2 px-3 h-11 rounded-xl bg-white/[0.05] border border-amber-400/20">
                <Lock size={16} className="text-amber-300/60" />
                <input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} placeholder="Current password" className="flex-1 bg-transparent outline-none text-[14px] text-white placeholder:text-white/35" />
              </div>
            </div>
          )}

          {error && <p className="text-[12px] text-red-400">{error}</p>}

          <button type="submit" disabled={busy} className="w-full h-12 rounded-xl bg-white text-black text-[14px] font-semibold flex items-center justify-center gap-2 hover:bg-white/85 disabled:opacity-60">
            {busy && <Loader2 size={16} className="animate-spin" />}
            Save changes
          </button>
        </form>
      </div>
    </div>
  );
}
